import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import CyberLayout from '@/components/CyberLayout';
import { toast } from 'sonner';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Lock, ArrowRight, Loader2, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      navigate('/admin/dashboard');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password
      });

      if (response.data.success) {
        localStorage.setItem('adminToken', response.data.token);
        navigate('/admin/dashboard');
      }
    } catch (error) {
      console.error(error);
      toast.error('Credenziali non valide');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CyberLayout>
      <div className="min-h-screen flex flex-col items-center justify-center px-6 py-20">
        <div className="max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-10 fade-in-up">
            {/* Logo */}
            <div className="mb-8">
              <img 
                src="/logo-white.svg" 
                alt="Smart Info" 
                className="h-16 mx-auto"
              />
            </div>
            
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cyber-gray border border-neon-red/30 mb-4">
              <Lock className="w-8 h-8 text-neon-red" />
            </div>
            <h1 
              className="font-orbitron text-2xl font-bold text-white mb-2 tracking-wider"
              data-testid="admin-login-title"
            >
              {t('adminLogin')}
            </h1>
            <p className="font-mono text-xs text-gray-500">
              RESTRICTED ACCESS
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="cyber-card rounded-lg p-8 fade-in-up" style={{ animationDelay: '0.1s' }}>
              <div className="space-y-6">
                <div>
                  <label className="font-mono text-xs text-neon-green/70 uppercase tracking-wider block mb-2">
                    {t('username')}
                  </label>
                  <Input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    data-testid="admin-username"
                    className="cyber-input w-full h-12 rounded-md"
                    placeholder="admin"
                    required
                  />
                </div>

                <div>
                  <label className="font-mono text-xs text-neon-green/70 uppercase tracking-wider block mb-2">
                    {t('password')}
                  </label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="admin-password"
                    className="cyber-input w-full h-12 rounded-md"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 fade-in-up" style={{ animationDelay: '0.2s' }}>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="cyber-btn-outline px-6 py-3 flex items-center justify-center gap-2 rounded-md flex-1"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>{t('back')}</span>
              </button>
              
              <button
                type="submit"
                disabled={loading}
                data-testid="admin-login-btn"
                className="cyber-btn px-6 py-3 flex items-center justify-center gap-2 flex-1"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <span>{t('login')}</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Security Note */}
          <div className="mt-8 text-center font-mono text-xs text-gray-600 fade-in-up" style={{ animationDelay: '0.3s' }}>
            <div className="flex items-center justify-center gap-2">
              <div className="w-2 h-2 rounded-full bg-neon-green animate-pulse" />
              SECURE_CONNECTION_ACTIVE
            </div>
          </div>
        </div>
      </div>
    </CyberLayout>
  );
}
